// Project backlog records: real records whose identity exists before a day is
// chosen. Scheduling moves the same uid into a day document; it never clones it.
(function (g: any) {
  'use strict';

  const KEY = 'md:projectRecords';

  function valid(row: any): boolean {
    return !!row && typeof row === 'object' && typeof row.uid === 'string' && !!row.uid
      && typeof row.pid === 'string' && !!row.pid;
  }

  function read(): any[] {
    const raw = g.mdRead(KEY, []);
    return Array.isArray(raw) ? raw.filter(valid) : [];
  }

  /* THE WRITE ANNOUNCES ITSELF (OD-1). Every mutating export passes through
     here exactly once, so one dispatch here is one event per mutation — and it
     fires only when the store actually moved, because a subscriber's whole job
     is re-reading the index and an event for a refused write would repaint the
     old truth and call it new. Undo and the sync bridge rewrite the key
     without passing through this module; md-app's `mdhistorychange` re-read
     covers those two hands, not this one. */
  function write(rows: any[]): boolean {
    const ok = g.mdWrite(KEY, rows);
    if (ok && typeof g.dispatchEvent === 'function' && typeof g.CustomEvent === 'function') {
      g.dispatchEvent(new g.CustomEvent('md:project-records-written', { detail: { at: Date.now() } }));
    }
    return ok;
  }

  function mdProjectRecordIndex(projectId?: unknown): any[] {
    const rows = read();
    return typeof projectId === 'string' ? rows.filter((row) => row.pid === projectId) : rows;
  }

  function mdProjectRecordById(uid: unknown): any | null {
    return typeof uid === 'string' ? read().find((row) => row.uid === uid) || null : null;
  }

  function mdProjectRecordCreate(projectId: unknown, text?: unknown): any | null {
    const project = g.mdProjectById ? g.mdProjectById(projectId) : null;
    if (!project || project.builtin) return null;
    const body = typeof text === 'string' ? text : '';
    const parts = g.mdRecordParts ? g.mdRecordParts(body) : { name: body.trim(), body: '' };
    const row = { uid: g.mdRecordUid(), pid: project.id, domain: project.domainId,
      name: parts.name || 'New Record', body: parts.body || '', kind: 'event', record: true,
      unscheduled: true, created: Date.now(), origin: g.mdRecordOriginStamp(),
      at: Date.now() };
    write([...read(), row]);
    return row;
  }

  /** The compose sheet's door (OD-3): an upsert by the CALLER's uid, because
      the sheet names its record before the store has it — the same uid must
      survive scheduling, status moves and ⌘Z, so the store cannot mint one.

      Creation obeys mdProjectRecordCreate's law (a real, unlocked project);
      an update keeps `created` and `origin` from the first commit, because
      the sheet rebuilds its record on every keystroke and a fact that moved
      with the rebuild would silently re-date the writing. Domain comes from
      the project, never from the caller — the same pair mdProjectRecordSetProject
      keeps together. One write(), so one announcement, and a refusal is silent. */
  function mdProjectRecordCommit(input: any): boolean {
    if (!input || typeof input !== 'object'
      || typeof input.uid !== 'string' || !input.uid) return false;
    const project = g.mdProjectById ? g.mdProjectById(input.pid) : null;
    if (!project) return false;
    const rows = read();
    const existing = rows.find((row) => row.uid === input.uid);
    if (!existing && project.builtin) return false;
    const links = Array.isArray(input.links) && input.links.length ? input.links : null;
    const row = {
      uid: input.uid, pid: project.id, domain: project.domainId,
      name: (typeof input.name === 'string' && input.name) || 'New Record',
      body: typeof input.body === 'string' ? input.body : '',
      kind: 'event', record: true, unscheduled: true,
      created: existing ? existing.created
        : (typeof input.created === 'number' ? input.created : Date.now()),
      origin: existing ? existing.origin
        : (input.origin || (g.mdRecordOriginStamp ? g.mdRecordOriginStamp() : undefined)),
      at: Date.now(),
      ...(links ? { links } : {}),
    };
    return write(existing ? rows.map((item) => (item.uid === input.uid ? row : item))
      : [...rows, row]);
  }

  function mdProjectRecordWrite(uid: unknown, text: unknown, links?: unknown): boolean {
    const rows = read();
    const row = rows.find((item) => item.uid === uid);
    if (!row) return false;
    const body = typeof text === 'string' ? text : '';
    const parts = g.mdRecordParts ? g.mdRecordParts(body) : { name: body.trim(), body: '' };
    row.name = parts.name || 'New Record';
    row.body = parts.body || '';
    const clean = g.mdWikiBindings ? g.mdWikiBindings(links, body) : [];
    if (clean.length) row.links = clean; else delete row.links;
    row.at = Date.now();
    return write(rows);
  }

  /** File a backlog record under a different project. The domain moves with it,
      because a project owns its domain — the same pair the compose sheet writes
      when a day record is filed, kept together here so no caller can move one
      without the other.

      A CLEAR IS REFUSED RATHER THAN OBEYED. `valid` requires a pid, so a row
      that lost one is filtered out of existence on the very next read: obeying
      would not unfile the record, it would delete it. Saying no out loud is the
      honest version of that, and it is the one place where this door and the
      day-record door disagree — a day record has a day to fall back to.

      A BUILT-IN PROJECT IS A LEGAL DESTINATION. mdProjectRecordCreate refuses
      one because a locked project's backlog is the app's to write, not the
      user's; moving a record the user already wrote into it is filing, and
      filing is theirs.

      WHERE THE RECORD STANDS DOES NOT MOVE, because it was never in the folder
      to begin with. The word lives in one flat document keyed by the record
      itself (WR-2), so filing a record somewhere else is a change of pid and
      nothing more — and the word survives even when the destination's workflow
      has never heard of it, reading as that workflow's Inbox until the record
      is filed back somewhere that knows it. */
  function mdProjectRecordSetProject(uid: unknown, projectId: unknown): boolean {
    const project = g.mdProjectById ? g.mdProjectById(projectId) : null;
    if (!project) return false;
    const rows = read();
    const row = rows.find((item) => item.uid === uid);
    if (!row) return false;
    if (row.pid === project.id) return true;
    row.pid = project.id;
    row.domain = project.domainId;
    row.at = Date.now();
    return write(rows);
  }

  /* ── the same move, with the refusal said out loud ────────────────────────
     `false` here means "no such record" and "no such project" at once, and the
     screen that offers this — a captured thought filed under the wrong project —
     needs to tell the two apart. A DOOR ONTO THE SETTER, not a copy of it. */
  function mdProjectRecordTrySetProject(uid: unknown, projectId: unknown):
    { ok: true } | { ok: false; reason: 'missing' | 'invalid' | 'storage_full' } {
    if (!mdProjectRecordById(uid)) return { ok: false, reason: 'missing' };
    if (!(g.mdProjectById && g.mdProjectById(projectId))) return { ok: false, reason: 'missing' };
    if (mdProjectRecordSetProject(uid, projectId)) return { ok: true };
    const fault = typeof g.mdStorageHealth === 'function' ? g.mdStorageHealth().lastFault : null;
    return { ok: false, reason: fault && fault.quota ? 'storage_full' : 'invalid' };
  }

  /** Move one backlog record to another status.

      A DOOR ONTO THE ONE DOOR (WR-3). This used to write `status` onto the
      backlog row itself, which is the home the shipped build used and WR-2
      retired — a second writer reaching a second place is how a record ends up
      wearing two words. The rule about what a move writes, and the day it ticks
      in, lives in one function for a record wherever it lives. */
  function mdProjectRecordSetStatus(uid: unknown, status: unknown): boolean {
    const record = mdProjectRecordById(uid);
    if (!record) return false;
    return !!(g.mdRecordTrySetStatus && g.mdRecordTrySetStatus(record, status).ok);
  }

  /* ── the same move, with the refusal said out loud ────────────────────────
     `false` here means "no such record" and "not a status of its workflow" at
     once, and a board that refuses a drop has to tell the two apart to say
     anything useful about it. A DOOR ONTO THE SETTER, not a copy of it. */
  function mdProjectRecordTrySetStatus(uid: unknown, status: unknown):
    { ok: true } | { ok: false; reason: 'missing' | 'invalid' | 'storage_full' } {
    if (!mdProjectRecordById(uid)) return { ok: false, reason: 'missing' };
    if (mdProjectRecordSetStatus(uid, status)) return { ok: true };
    const fault = typeof g.mdStorageHealth === 'function' ? g.mdStorageHealth().lastFault : null;
    return { ok: false, reason: fault && fault.quota ? 'storage_full' : 'invalid' };
  }

  /** Where this record LIVES, generalised (0.2).

      `pid` was the whole answer while a record could only ever live in a
      project. A record may now contain a sub-record, so the pointer is
      `parentRef` — a ref key, like a desk's `scopeRef` — and `pid` becomes what
      it always meant underneath: the nearest project above, so the backlog a
      record is listed in stays true without anybody writing it twice.

      Both are written together, by md-parent.ts, which is the only caller that
      knows the tree. A record whose parent IS its project keeps `parentRef`
      absent rather than writing the same fact twice — so nothing already stored
      changes shape, and an older build reading this document still finds the
      `pid` it expects. */
  function mdProjectRecordSetParent(uid: unknown, parentRef: unknown,
    projectId: unknown): boolean {
    const rows = read();
    const row = rows.find((item) => item.uid === uid);
    if (!row) return false;
    const parent = typeof parentRef === 'string' && parentRef ? parentRef : '';
    const pid = typeof projectId === 'string' && projectId ? projectId : '';
    /* `pid` is what makes a row valid at all (see `valid` above), so a record
       whose chain reaches no project cannot be written here. That is not a
       silent refusal: it is the backlog saying a record has to be in some
       project's backlog to be one of its rows. */
    if (!pid) return false;
    if (parent && parent !== `project:${pid}`) row.parentRef = parent;
    else delete row.parentRef;
    row.pid = pid;
    const project = g.mdProjectById ? g.mdProjectById(pid) : null;
    if (project) row.domain = project.domainId;
    row.at = Date.now();
    return write(rows);
  }

  function mdProjectRecordRemove(uid: unknown): boolean {
    const rows = read();
    if (!rows.some((row) => row.uid === uid)) return false;
    return write(rows.filter((row) => row.uid !== uid));
  }

  function mdProjectRecordSyncDocs(): Record<string, unknown> {
    const rows = read();
    return rows.length ? { 'project-records': rows } : {};
  }

  function mdProjectRecordApplyDoc(key: unknown, value: unknown): boolean {
    if (key !== 'project-records' || !Array.isArray(value)) return false;
    return write(value.filter(valid));
  }

  Object.assign(g, { mdProjectRecordIndex, mdProjectRecordById, mdProjectRecordCreate,
    mdProjectRecordCommit, mdProjectRecordTrySetProject, mdProjectRecordSetStatus, mdProjectRecordTrySetStatus,
    mdProjectRecordWrite, mdProjectRecordSetProject, mdProjectRecordSetParent,
    mdProjectRecordRemove,
    mdProjectRecordSyncDocs, mdProjectRecordApplyDoc });
})(typeof window !== 'undefined' ? window : globalThis);
